import { useEffect, useState } from "react";
import { msUntilNextPuzzle } from "./game";

// Tablets and wider get the desktop layout; anything narrower is the phone
// layout. Keep in step with the media queries in daily.css.
export const DESKTOP_QUERY = "(min-width: 768px)";

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

// Height of the on-screen keyboard, so the guess card can sit above it.
export const useKeyboardInset = (): number => {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const onResize = () =>
      setInset(
        Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      );
    onResize();
    viewport.addEventListener("resize", onResize);
    viewport.addEventListener("scroll", onResize);
    return () => {
      viewport.removeEventListener("resize", onResize);
      viewport.removeEventListener("scroll", onResize);
    };
  }, []);

  return inset;
};

export const useNextPuzzleCountdown = (): number => {
  const [remaining, setRemaining] = useState(msUntilNextPuzzle);

  useEffect(() => {
    const timer = setInterval(() => setRemaining(msUntilNextPuzzle()), 1000);
    return () => clearInterval(timer);
  }, []);

  return remaining;
};

export const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Animates a number from 0 to 1 over the given time, easing out. Restarts
// whenever `key` changes; stays at 1 when motion is reduced.
export const useProgress = (key: string | null, durationMs: number): number => {
  const [progress, setProgress] = useState(1);

  useEffect(() => {
    if (key === null || prefersReducedMotion()) {
      setProgress(1);
      return;
    }
    setProgress(0);
    let frame = 0;
    const startedAt = performance.now();
    const tick = (now: number) => {
      const linear = Math.min(1, (now - startedAt) / durationMs);
      setProgress(1 - (1 - linear) ** 3);
      if (linear < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [key, durationMs]);

  return progress;
};

// Colours the browser's own bars to match the screen. Mobile Safari tints
// its status bar and address bar from the page's theme colour, or failing
// that the body's background, which is otherwise the classic game's dark
// blue. Pass null to leave things alone. Put back when the screen goes.
export const usePageColour = (colour: string | null): void => {
  useEffect(() => {
    if (!colour) return;

    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const added = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    const previousTheme = meta.content;
    const previousBody = document.body.style.backgroundColor;
    const previousRoot = document.documentElement.style.backgroundColor;

    meta.content = colour;
    document.body.style.backgroundColor = colour;
    document.documentElement.style.backgroundColor = colour;

    return () => {
      if (added) meta.remove();
      else meta.content = previousTheme;
      document.body.style.backgroundColor = previousBody;
      document.documentElement.style.backgroundColor = previousRoot;
    };
  }, [colour]);
};
